import React, { useCallback, useRef, useState } from 'react';

import './css/StateOfTheArt.css'

const ORIGINAL_IMAGE: string = require( './images/temple.jpg' )
const WATERMARKED_IMAGE: string = require( './images/temple_0.2.jpg' )

// 25-symbol hex string (4 bits/symbol) demoing a real payload embedded in the
// watermark. Also shown in the Server section's detect output, so the story
// connects: embed -> leak -> detect.
export const WATERMARK_MESSAGE: string = '3F0A9C2E7B41D8FA6C925E71D'

// What /detect answers on that copy: whether the mark is there, and how sure
// the decoder is. Printed both under the slider and in the Server terminal,
// declared once so the two cannot disagree.
export const DETECT_CONFIDENCE: string = '0.99'

export function CompareSlider(): React.JSX.Element {

	const containerRef = useRef<HTMLDivElement>( null )
	const [ position, setPosition ] = useState<number>( 50 )
	const draggingRef = useRef<boolean>( false )

	const updatePosition = useCallback( ( clientX: number ) => {
		const container = containerRef.current
		if ( !container ) return
		const rect = container.getBoundingClientRect()
		const ratio = ( clientX - rect.left ) / rect.width
		setPosition( Math.min( 100, Math.max( 0, ratio * 100 ) ) )
	}, [] )

	const onPointerDown = useCallback( ( event: React.PointerEvent<HTMLDivElement> ) => {
		draggingRef.current = true;
		( event.target as HTMLElement ).setPointerCapture( event.pointerId )
		updatePosition( event.clientX )
	}, [ updatePosition ] )

	const onPointerMove = useCallback( ( event: React.PointerEvent<HTMLDivElement> ) => {
		if ( !draggingRef.current ) return
		updatePosition( event.clientX )
	}, [ updatePosition ] )

	const onPointerUp = useCallback( ( event: React.PointerEvent<HTMLDivElement> ) => {
		draggingRef.current = false;
		( event.target as HTMLElement ).releasePointerCapture( event.pointerId )
	}, [] )

	return (
		<div
			ref={ containerRef }
			className="CompareSlider"
			onPointerDown={ onPointerDown }
			onPointerMove={ onPointerMove }
			onPointerUp={ onPointerUp }
		>
			<img src={ WATERMARKED_IMAGE } className="CompareSlider-Image" alt="Watermarked" draggable={ false } />
			<div className="CompareSlider-ClippedLayer" style={ { clipPath: `inset(0 ${ 100 - position }% 0 0)` } } >
				<img src={ ORIGINAL_IMAGE } className="CompareSlider-Image" alt="Original" draggable={ false } />
			</div>

			<div className="CompareSlider-Label CompareSlider-Label-Left fs-18" >Original</div>
			<div className="CompareSlider-Label CompareSlider-Label-Right fs-18" >Watermarked</div>

			<div className="CompareSlider-Handle" style={ { left: `${ position }%` } } >
				<div className="CompareSlider-HandleKnob" >
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" >
						<path d="M5 2L1.5 7L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						<path d="M9 2L12.5 7L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</div>
			</div>
		</div>
	)
}

export function StateOfTheArt(): React.JSX.Element {

	return (
		<div className="Pane-Light StateOfTheArt" >
			<div className="Delimiter" />
			<div className="Container" >
				<div className="Title text-shadow-2" data-fade >You can't see it. You can always find it.</div>
				<div className="Content" >

					<div className="StateOfTheArt-SliderWrapper" data-fade >
						<CompareSlider />
					</div>

					{ /* What comes back when that same copy is handed to the service:
					     a verdict, how sure the decoder is, and the identifier it
					     read out of the pixels. */ }
					<div className="StateOfTheArt-Detect" data-fade data-fade-delay="0.1" >
						<div className="StateOfTheArt-Detect-Head fs-16" >detect · the watermarked copy above</div>
						<div className="StateOfTheArt-Detect-Fields" >
							<div className="StateOfTheArt-Detect-Field" >
								<span className="StateOfTheArt-Detect-Key fs-16" >watermark</span>
								<span className="StateOfTheArt-Detect-Value StateOfTheArt-Detect-Found fs-16" >found</span>
							</div>
							<div className="StateOfTheArt-Detect-Field" >
								<span className="StateOfTheArt-Detect-Key fs-16" >confidence</span>
								<span className="StateOfTheArt-Detect-Value fs-16" >{ DETECT_CONFIDENCE }</span>
							</div>
							<div className="StateOfTheArt-Detect-Field StateOfTheArt-Detect-Field-Wide" >
								<span className="StateOfTheArt-Detect-Key fs-16" >identifier</span>
								<span className="StateOfTheArt-Detect-Value fs-16" >{ WATERMARK_MESSAGE }</span>
							</div>
						</div>
					</div>

					<div className="StateOfTheArt-Note fs-18 Text-Centered" data-fade data-fade-delay="0.15" >
						The identifier is invisible to the eye and built to survive the trip: compression, resizing, cropping, format conversion and re-encoding. Hand any copy back to the service and it returns the verdict, a confidence score and the identifier itself — which is what turns a file found somewhere into the account it was issued to.
					</div>

				</div>
			</div>
		</div>
	)
}
